import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null);

  // Helper to build user data with roles support
  const buildUserData = (authUser, metadata = {}) => {
    const primaryRole = metadata.role || authUser.user_metadata?.role || 'student';
    const roles = metadata.roles || authUser.user_metadata?.roles || [primaryRole];
    
    // Ensure primary role is always in roles array
    const allRoles = roles.includes(primaryRole) ? roles : [primaryRole, ...roles];
    
    return {
      id: authUser.id,
      email: authUser.email,
      name: metadata.full_name || authUser.user_metadata?.full_name || authUser.email.split('@')[0],
      role: primaryRole,  // Primary role (default)
      roles: allRoles,    // All assigned roles
      avatar: `https://ui-avatars.com/api/?name=${metadata.full_name || authUser.user_metadata?.full_name || authUser.email.split('@')[0]}&background=0891b2&color=fff`
    };
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        // Get user metadata from localStorage or fetch from backend
        const savedUser = localStorage.getItem('fourq_user');
        const savedActiveRole = localStorage.getItem('fourq_active_role');
        
        if (savedUser) {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          // Restore active role or default to primary role
          setActiveRole(savedActiveRole || parsedUser.role);
        } else {
          // Create user object from session
          const userData = buildUserData(session.user);
          setUser(userData);
          setActiveRole(userData.role);
          localStorage.setItem('fourq_user', JSON.stringify(userData));
          localStorage.setItem('fourq_active_role', userData.role);
        }
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        const userData = buildUserData(session.user);
        setUser(userData);
        
        // Preserve active role if it's still valid, otherwise default to primary
        const savedActiveRole = localStorage.getItem('fourq_active_role');
        const newActiveRole = savedActiveRole && userData.roles.includes(savedActiveRole) 
          ? savedActiveRole 
          : userData.role;
        setActiveRole(newActiveRole);
        
        localStorage.setItem('fourq_user', JSON.stringify(userData));
        localStorage.setItem('fourq_active_role', newActiveRole);
      } else {
        setUser(null);
        setActiveRole(null);
        localStorage.removeItem('fourq_user');
        localStorage.removeItem('fourq_active_role');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      const userData = buildUserData(data.user);
      
      setUser(userData);
      setActiveRole(userData.role);  // Default to primary role on login
      localStorage.setItem('fourq_user', JSON.stringify(userData));
      localStorage.setItem('fourq_active_role', userData.role);
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      // Use window.location.origin for OAuth redirect to work across all environments
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      console.log('OAuth redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) throw error;
      
      // OAuth will redirect, so we don't need to return anything
      return data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const signup = async (email, password, name, role, phone = '') => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            role: role,
            roles: [role],  // Initialize with single role
            phone: phone
          }
        }
      });

      if (error) throw error;

      const userData = {
        id: data.user.id,
        email: data.user.email,
        name: name,
        role: role,
        roles: [role],
        phone: phone,
        avatar: `https://ui-avatars.com/api/?name=${name}&background=0891b2&color=fff`
      };
      
      setUser(userData);
      setActiveRole(role);
      localStorage.setItem('fourq_user', JSON.stringify(userData));
      localStorage.setItem('fourq_active_role', role);
      return userData;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setActiveRole(null);
      localStorage.removeItem('fourq_user');
      localStorage.removeItem('fourq_active_role');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const switchRole = (newRole) => {
    // Switch to a different role (must be in user's roles array)
    if (!user) return;
    
    // Check if user has this role
    if (!user.roles || !user.roles.includes(newRole)) {
      console.error('User does not have this role:', newRole);
      return;
    }
    
    setActiveRole(newRole);
    localStorage.setItem('fourq_active_role', newRole);
  };

  // Refresh user data from backend (useful after admin updates roles)
  const refreshUserData = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/profile`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );
      
      if (response.ok) {
        const profileData = await response.json();
        const updatedUser = {
          ...user,
          roles: profileData.roles || [profileData.role || user.role],
          role: profileData.role || user.role
        };
        setUser(updatedUser);
        localStorage.setItem('fourq_user', JSON.stringify(updatedUser));
        
        // If active role is no longer valid, switch to primary
        if (!updatedUser.roles.includes(activeRole)) {
          setActiveRole(updatedUser.role);
          localStorage.setItem('fourq_active_role', updatedUser.role);
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const getAccessToken = () => {
    return session?.access_token || null;
  };

  const value = {
    user,
    session,
    loading,
    activeRole,      // Currently active role for dashboard view
    login,
    loginWithGoogle,
    signup,
    logout,
    switchRole,      // Switch between assigned roles
    refreshUserData, // Refresh roles from backend
    getAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};